import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import ContactSection from '../components/ContactSection';
import { Scan, CheckCircle2, Target, Layers, FileText } from 'lucide-react';
import { breadcrumbSchema } from '../utils/structuredData';

const PointCloudSurveysPage = () => {
  const features = [
    'Survey-grade accuracy to within 2mm',
    'Registered and geo-referenced point cloud datasets',
    'Coloured point clouds with HDR photography overlay',
    '2D CAD drawings (plans, sections, elevations)',
    'Revit BIM models at LOD 200-500',
    'Deliverables in all major formats (RCP, E57, LAS, DWG)'
  ];

  const benefits = [
    {
      icon: Target,
      title: 'Sub-Millimetre Accuracy',
      description: 'Laser scanners capture millions of measurements per second, producing datasets accurate to within 2mm at typical survey ranges.'
    },
    {
      icon: Layers,
      title: 'Complete Spatial Data',
      description: 'Every surface, junction and detail is captured in a single visit — nothing is missed and nothing needs to be re-measured.'
    },
    {
      icon: FileText,
      title: 'Flexible Deliverables',
      description: 'From raw point clouds to fully modelled BIM, we tailor outputs to your workflow and project requirements.'
    }
  ];

  const useCases = [
    {
      title: 'As-Built Surveys',
      description: 'Capture the existing condition of any building or structure in precise 3D. As-built point cloud surveys provide architects, engineers and contractors with an accurate baseline for design, renovation and refurbishment work. Unlike traditional measured surveys, point cloud data captures every surface and detail, eliminating the risk of missed measurements or return visits.'
    },
    {
      title: 'Renovation and Refurbishment',
      description: 'Before any renovation project begins, understanding the true condition and geometry of the existing building is essential. Point cloud surveys reveal structural deviations, floor-to-ceiling heights, service routes and spatial relationships that may not be apparent from original drawings — particularly in older buildings where as-built records are incomplete or inaccurate.'
    },
    {
      title: 'Heritage and Conservation',
      description: 'Listed buildings, historic structures and conservation areas require particularly sensitive and thorough documentation. Point cloud scanning provides a non-contact method of recording every architectural detail — mouldings, stonework, timber framing — to sub-millimetre accuracy. The resulting dataset serves as both a record and a design reference for sympathetic restoration work.'
    },
    {
      title: 'MEP and Services Coordination',
      description: 'Mechanical, electrical and plumbing services are among the most complex elements to coordinate in any building project. Point cloud surveys capture the exact position and routing of existing services, enabling clash detection and coordination in 3D before work begins on site. This is particularly valuable in refurbishment projects where services are concealed above ceilings or within risers.'
    }
  ];

  const faqs = [
    {
      question: 'What is a point cloud survey?',
      answer: 'A point cloud survey uses 3D laser scanning technology to capture millions of precise spatial measurements of a building or structure. The result is a dense cloud of data points that represents every visible surface in accurate 3D. This data can be used for measurement, modelling, analysis and visualisation.'
    },
    {
      question: 'What is the difference between laser scanning and photogrammetry?',
      answer: 'Laser scanning uses a rotating laser to directly measure distances to surrounding surfaces, producing highly accurate point cloud data regardless of lighting conditions. Photogrammetry uses overlapping photographs and software algorithms to reconstruct 3D geometry. Laser scanning is generally more accurate and reliable for interior surveys, while photogrammetry can be more efficient for large exterior areas or facades.'
    },
    {
      question: 'How accurate is a point cloud survey?',
      answer: 'Using survey-grade Leica and FARO laser scanners, our point cloud surveys achieve accuracy of 2mm or better at typical working ranges. For projects requiring higher precision, we can deploy targets and control networks to achieve sub-millimetre accuracy across large sites.'
    },
    {
      question: 'What deliverables do I receive from a point cloud survey?',
      answer: 'Standard deliverables include registered and coloured point cloud datasets in industry-standard formats (RCP, E57, LAS). We also offer 2D CAD drawings, 3D Revit BIM models, floor plans, sections and elevations. Deliverables are tailored to your project requirements.'
    },
    {
      question: 'How long does a point cloud survey take on site?',
      answer: 'A single room can be scanned in 15-20 minutes per scan position. A typical commercial floor of 500 sqm requires 15-25 scan positions and can be completed in half a day. Larger buildings or multi-storey surveys are typically completed within 1-3 days depending on complexity and access arrangements.'
    },
    {
      question: 'Can point cloud data be converted into a BIM model?',
      answer: 'Yes. Point cloud data is the foundation for our Scan to BIM service. We use the registered point cloud as a reference to model the building in Autodesk Revit at the Level of Development (LOD) your project requires. This is the most common workflow for renovation and refurbishment projects that require accurate as-built BIM models.'
    }
  ];

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Point Cloud Surveys",
    "name": "Point Cloud Surveys London",
    "provider": {
      "@type": "Organization",
      "name": "Digital Scanning Group",
      "url": "https://www.digitalscanninggroup.com"
    },
    "areaServed": [
      { "@type": "Place", "name": "London" },
      { "@type": "Place", "name": "United Kingdom" }
    ],
    "description": "Professional point cloud laser scanning surveys in London. Survey-grade accuracy for as-built documentation, renovation planning and BIM modelling.",
    "offers": {
      "@type": "Offer",
      "priceCurrency": "GBP",
      "price": "495",
      "priceValidUntil": "2027-12-31",
      "availability": "https://schema.org/InStock"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Point Cloud Surveys London | Laser Scanning & 3D Survey Services"
        description="Professional point cloud surveys in London. Laser scanning with 2mm accuracy for as-built surveys, renovation and BIM. From £495."
        keywords="point cloud survey london, laser scanning london, 3D survey london, as-built survey, point cloud scanning, laser survey london"
        canonicalUrl={typeof window !== 'undefined' ? `${window.location.origin}/point-cloud-surveys-london` : ''}
        structuredData={[
          serviceSchema,
          faqSchema,
          breadcrumbSchema([
            { name: 'Home', url: typeof window !== 'undefined' ? window.location.origin : '' },
            { name: 'Point Cloud Surveys London', url: typeof window !== 'undefined' ? `${window.location.origin}/point-cloud-surveys-london` : '' }
          ])
        ]}
      />
      <Navbar scrolled={true} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-blue-50 rounded-full mb-6">
              <Scan className="h-6 w-6 text-blue-600" />
              <span className="text-blue-600 font-semibold">POINT CLOUD SCANNING</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Point Cloud Surveys London
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Capture the precise geometry of any building or structure with survey-grade 3D laser scanning. Our point cloud surveys deliver accurate, comprehensive spatial data for design, construction and asset management.
            </p>
            <div className="mt-8">
              <Link
                to="/pricing"
                className="inline-block px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-blue-500/50 transition-all duration-300"
              >
                Get a Quote
              </Link>
            </div>
          </div>

          {/* Hero Image */}
          <div className="relative rounded-2xl overflow-hidden shadow-2xl mb-16">
            <img
              src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxsYXNlciUyMHNjYW5uaW5nJTIwYnVpbGRpbmd8ZW58MHx8fHw&ixlib=rb-4.1.0&q=85&w=1400"
              alt="Point Cloud Laser Scanning Survey in Progress"
              className="w-full h-[500px] object-cover"
            />
          </div>

          {/* What is Point Cloud Scanning */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start mb-20">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                What Is Point Cloud Scanning?
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Point cloud scanning is a method of capturing the three-dimensional geometry of a physical space using laser scanning technology. A terrestrial laser scanner emits millions of laser pulses per second, measuring the distance to every visible surface with millimetre-level accuracy. The result is a dense collection of 3D coordinates — known as a point cloud — that represents the scanned environment in precise spatial detail.
                </p>
                <p>
                  At Digital Scanning Group, we use survey-grade laser scanners from Leica and FARO to capture buildings, structures and sites across London and the UK. Each scan position captures up to one million points per second, and multiple positions are registered together to create a complete, unified dataset. The point cloud can be coloured using integrated HDR photography, providing both geometric accuracy and visual context.
                </p>
                <p>
                  Point cloud data serves as the foundation for a wide range of downstream deliverables. It can be used directly for measurement and analysis, or processed into 2D CAD drawings, 3D <Link to="/scan-to-bim-london" className="text-blue-600 hover:underline">Revit BIM models</Link>, floor plans, sections and elevations. Because the data is captured comprehensively in a single site visit, there is no need to return to site to take additional measurements — everything is already in the dataset. For projects requiring BIM, our guide to <Link to="/blog/bim-level-2-requirements-uk" className="text-blue-600 hover:underline">BIM Level 2 requirements in the UK</Link> explains the standards and workflows involved.
                </p>
                <p>
                  Point cloud surveys also form part of a broader <Link to="/blog/what-is-a-digital-twin" className="text-blue-600 hover:underline">digital twin</Link> strategy. When combined with <Link to="/matterport-virtual-tours-london" className="text-blue-600 hover:underline">Matterport virtual tours</Link> and ongoing monitoring, the data provides a comprehensive spatial record that supports decision-making throughout the building lifecycle. If you are unsure which scanning technology suits your project, our article on <Link to="/blog/matterport-vs-laser-scanning-which-do-you-need" className="text-blue-600 hover:underline">Matterport vs laser scanning</Link> explains the key differences.
                </p>
                <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3">Laser Scanning vs Photogrammetry</h3>
                <p>
                  While both laser scanning and photogrammetry produce 3D spatial data, they use fundamentally different methods. Laser scanning directly measures distances using time-of-flight or phase-shift technology, producing reliable results in any lighting condition. Photogrammetry derives 3D geometry from overlapping photographs, which can be affected by lighting, reflective surfaces and texture. For interior surveys and projects requiring guaranteed accuracy, laser scanning is the preferred method. Photogrammetry excels for large-scale exterior mapping, facades and aerial surveys where drone-mounted cameras can cover large areas efficiently.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Survey Deliverables</h3>
              <div className="space-y-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                    <CheckCircle2 className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 font-medium">{feature}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/pricing"
                className="mt-8 w-full inline-block text-center px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-blue-500/50 transition-all duration-300"
              >
                Get a Quote
              </Link>
            </div>
          </div>

          {/* Benefits Section */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Why Choose Point Cloud Surveys</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div key={index} className="p-8 bg-blue-50 rounded-2xl text-center">
                    <div className="inline-flex p-4 bg-blue-100 rounded-xl mb-4">
                      <Icon className="h-8 w-8 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Use Cases */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">Point Cloud Survey Applications</h2>
            <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
              From as-built documentation to heritage conservation, point cloud surveys provide the spatial foundation for projects of every type and scale.
            </p>
            <div className="space-y-6">
              {useCases.map((useCase, index) => (
                <div key={index} className="bg-white border-2 border-gray-100 rounded-2xl p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{useCase.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{useCase.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Related Services */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">Related Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Link to="/scan-to-bim-london" className="p-6 bg-white border-2 border-gray-100 rounded-2xl hover:border-blue-300 transition-colors">
                <Layers className="h-8 w-8 text-blue-600 mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Scan to BIM</h3>
                <p className="text-gray-600 text-sm">Convert point cloud data into accurate Revit BIM models for design and construction.</p>
              </Link>
              <Link to="/matterport-virtual-tours-london" className="p-6 bg-white border-2 border-gray-100 rounded-2xl hover:border-blue-300 transition-colors">
                <Scan className="h-8 w-8 text-blue-600 mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Matterport Virtual Tours</h3>
                <p className="text-gray-600 text-sm">Immersive 3D virtual tours for property marketing and remote viewing.</p>
              </Link>
              <Link to="/construction" className="p-6 bg-white border-2 border-gray-100 rounded-2xl hover:border-blue-300 transition-colors">
                <Target className="h-8 w-8 text-blue-600 mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Construction Scanning</h3>
                <p className="text-gray-600 text-sm">3D reality capture for construction progress monitoring and as-built documentation.</p>
              </Link>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Frequently Asked Questions</h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white border-2 border-gray-100 rounded-2xl p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-12 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Need a Point Cloud Survey in London?</h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Contact us today for a free consultation. We provide detailed quotes within 24 hours and can mobilise to site at short notice.
            </p>
            <Link
              to="/pricing"
              className="inline-block px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-blue-500/50 transition-all duration-300"
            >
              Get a Quote
            </Link>
          </div>
        </div>
      </section>

      <ContactSection />
      <Footer />
    </div>
  );
};

export default PointCloudSurveysPage;
